import { useEffect, useRef } from "react";
import { useSessionSync } from "../hooks/useSessionSync";
import { useProfileMigration } from "../hooks/useProfileMigration";
import { useIncompleteProfileCheck } from "../hooks/useIncompleteProfileCheck";
import { useAuthStore } from "../stores/authStore";
import { isAuthed } from "../models/authState";
import { useShellStore } from "@/features/shell/stores/shellStore";
import {
  initialBetslipState,
  useBetslipStore,
} from "@/features/betslip/stores/betslipStore";
import { useAutoPruneByPermissions } from "@/features/betslip/hooks/useAutoPruneByPermissions";
import { usePickslipStore } from "@/features/pickems/stores/pickslipStore";
import localObject from "@/utilities/localObject";
import {
  useBiometricSync,
  useBiometricPromptCheck,
} from "../hooks/useBiometrics";
import { useBiometricAutoLogin } from "../hooks/useBiometricAutoLogin";
import { queryClient } from "utilities/queryClient";
import { teardownUserSubscriptions } from "@/hooks/firestore/core/subscription";

/**
 * Handles auth-related side effects:
 * - Session synchronization with server
 * - Profile migration for outdated versions
 * - Incomplete profile modal prompt
 * - Biometric enrollment sync, setup prompt, and auto-login on launch
 * - Logout state cleanup for betslip, pickslip, layout, and query cache
 * - Permissions-based betslip validation
 */
export const AuthHooks = () => {
  useSessionSync();
  useProfileMigration();
  useIncompleteProfileCheck();
  useBiometricSync();
  useBiometricPromptCheck();
  useBiometricAutoLogin();

  // Selections that lose their permission are dropped here rather than in the
  // betslip panel, so a revoked permission takes effect while the slip is shut.
  useAutoPruneByPermissions();

  const setModal = useShellStore((s) => s.setModal);
  const resetPickslip = usePickslipStore((s) => s.reset);
  const isLoggedIn = useAuthStore((s) => isAuthed(s));
  const wasLoggedIn = useRef(isLoggedIn);

  // Handle logout cleanup
  useEffect(() => {
    if (wasLoggedIn.current && !isLoggedIn) {
      // Hard-reset the betslip: this hook is always mounted, so
      // the previous user's selections, stakes, promo toggles and token
      // ids can never survive into the next session — even if the
      // betslip panel itself is closed (unmounted) at logout, or the
      // slip is mid-submission.
      useBetslipStore.setState(initialBetslipState);

      // Clear pickslip
      resetPickslip();

      // Reset layout
      useShellStore.getState().reset();
      setModal(null);

      // remove user preferences
      localObject.removeValue("userPreferences");

      // Kill the user's Firestore listeners before the auth token is revoked
      // and purge user-scoped query data (Firestore and REST hooks both tag
      // meta.userScoped); public queries keep streaming untouched.
      teardownUserSubscriptions();
      queryClient.removeQueries({
        predicate: (query) => query.meta?.userScoped === true,
      });
    }
    wasLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, setModal]);

  return null;
};
